import type { SupabaseClient } from "@supabase/supabase-js";

import { mapRosterRow, type RosterRow } from "./roster";

/** Existing management view only: no auth-user IDs, base tables, or new host contract. */
export const STAFF_WEEK_COLUMNS =
  "route_assignment_id, route_id, route_name, site_id, site_name, assignment_status, guard_label, guard_ref, starts_at, ends_at";
export const STAFF_WEEK_ROW_LIMIT = 500;
export const SAFE_STAFF_WEEK_FAILURE = "Could not load staff assignments. Please try again.";

export interface StaffWeekAssignment extends RosterRow {
  routeId: string | null;
}

export interface StaffWeekAssignments {
  rows: StaffWeekAssignment[];
  /** The read reached its cap; more rows may exist. Never a completeness guarantee. */
  truncated: boolean;
}

function instant(value: string): number {
  // Require an explicit offset: a device-local datetime must never move the query window.
  return /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? Date.parse(value) : Number.NaN;
}

/**
 * Overlapping assignments in a bounded window, using the same RLS-backed view as Command.
 * The caller supplies absolute instants, optionally padded around the display week for
 * timezone/DST safety, and filters the resulting calendar dates in its display timezone.
 *
 * This view retains scheduled/active rows but only recently ended historical rows. It is
 * not a complete historical schedule. Its public envelope has no shift_slot_id, and the
 * route/time fields must not be treated as an authoritative writable-slot association.
 * guard_ref can join directory member_ref only when that key is present and unambiguous;
 * labels are presentation, never an identity join. Existing directory/route adapters own
 * member and property/job picker reads separately.
 */
export async function fetchStaffWeekAssignments(
  client: SupabaseClient,
  accountId: string,
  fromInstant: string,
  toInstant: string,
): Promise<StaffWeekAssignments> {
  const from = instant(fromInstant);
  const to = instant(toInstant);
  // A seven-day week plus timezone padding fits in ten days. Refuse accidentally unbounded
  // or device-local requests before calling the host.
  if (!accountId.trim() || !Number.isFinite(from) || !Number.isFinite(to) || to <= from || to - from > 10 * 86_400_000) {
    throw new Error(SAFE_STAFF_WEEK_FAILURE);
  }
  try {
    const { data, error } = await client
      .from("management_on_shift_roster")
      .select(STAFF_WEEK_COLUMNS)
      .eq("account_id", accountId)
      .lt("starts_at", toInstant)
      .gt("ends_at", fromInstant)
      .order("starts_at", { ascending: true })
      .limit(STAFF_WEEK_ROW_LIMIT);
    if (error !== null || !Array.isArray(data)) throw new Error(SAFE_STAFF_WEEK_FAILURE);
    const rows = data.slice(0, STAFF_WEEK_ROW_LIMIT).map((raw: unknown): StaffWeekAssignment => {
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) throw new Error(SAFE_STAFF_WEEK_FAILURE);
      const record = raw as Record<string, unknown>;
      const row = mapRosterRow(record);
      if (!row.routeAssignmentId || row.startsAt === null || row.endsAt === null ||
        !Number.isFinite(instant(row.startsAt)) || !Number.isFinite(instant(row.endsAt)) ||
        instant(row.endsAt) <= instant(row.startsAt)) throw new Error(SAFE_STAFF_WEEK_FAILURE);
      return { ...row, routeId: typeof record.route_id === "string" && record.route_id.length > 0 ? record.route_id : null };
    });
    return { rows, truncated: data.length >= STAFF_WEEK_ROW_LIMIT };
  } catch {
    // Network errors and host details are never rendered as operator-facing text.
    throw new Error(SAFE_STAFF_WEEK_FAILURE);
  }
}
